(ns common.schema.listing-definition
  (:require
   [clojure.string :as string]
   [common.opensearch.days-since :as days-since]
   [common.opensearch.fields :as os-fields]
   [common.opensearch.filter-queries :as os-filter-queries]
   [common.opensearch.kanban-sort :as kanban-sort]
   [common.opensearch.trigram :as trigram]
   [common.opensearch.utils :as os-utils]
   [common.schema.entity-type-registry :as entity-type-registry]))

(defn normalize-field-path
  [field-path]
  (os-filter-queries/normalize-field-path field-path))

(defn entity-definition
  [entity-type]
  (entity-type-registry/definition entity-type))

(defn search-config
  [entity-type]
  (:search (entity-definition entity-type)))

(defn view-config
  [entity-type view-mode]
  (get-in (search-config entity-type) [:views view-mode]))

(defn- entity-field-schema
  [entity-type field-path]
  (let [attribute (some-> field-path normalize-field-path last)
        definition (entity-definition entity-type)]
    (some->> (:schema definition)
             (filter (fn [[field-attribute _]]
                       (= field-attribute attribute)))
             first
             second)))

(def ^:private multiline-text-data-type
  "A text of many lines. The index holds it as plain text, as it holds a one-line
   text, so a search in it finds the words of the text."
  :data.type/text-multiline)

(def ^:private richtext-data-type
  "A text with the markup of a rich text editor. The index holds the markup, so
   the names of the tags are words of the text: a search for `strong` finds each
   text that has a bold word. See `html-tag-query?`."
  :data.type/text-richtext)

(def ^:private html-tag-names
  "The tags that the rich text editors write into a text. A search for one of
   these words would find the markup, and not the text of the user. The list is
   the tags that can be in a text, and not every tag of HTML, so that a word like
   `video` or `form` that no rich text holds as a tag finds the text that has it."
  #{"a" "b" "big" "blockquote" "br" "caption" "center" "code" "col" "colgroup"
    "del" "div" "em" "figcaption" "figure" "font" "h1" "h2" "h3" "h4" "h5" "h6"
    "hr" "i" "img" "ins" "li" "mark" "ol" "p" "pre" "s" "small" "span" "strike"
    "strong" "sub" "sup" "table" "tbody" "td" "tfoot" "th" "thead" "tr" "u" "ul"})

(defn html-tag-query?
  "True when a word of `query-text` is the name of an HTML tag that a rich text can
   have, as `strong`, `<strong>` or `strong growth`. The toolbar search does not
   search the rich text fields for such a query. The words are the tokens that the
   index makes: the letters and the digits, without the signs around them. A query
   with more than one word matches a text that has any of the words, so one tag
   is enough to find the markup."
  [query-text]
  (boolean
   (some html-tag-names
         (re-seq #"[a-z0-9]+" (string/lower-case (or query-text ""))))))

(defn- field-path->query-field
  [field-path data-type {:keys [include-richtext?]}]
  (cond
    (contains? os-fields/labelled-data-types data-type)
    (os-utils/field-path->labels-alias field-path)

    (or (contains? os-filter-queries/text-like-data-types data-type)
        (= multiline-text-data-type data-type)
        (and include-richtext?
             (= richtext-data-type data-type)))
    (os-utils/field-path->flat-alias field-path)

    :else
    nil))

(defn searchable-query-fields
  "Returns accessible OpenSearch text/label fields, optionally restricted by
  `:query-field-paths` in the context. An explicitly empty scope returns no fields.
  The fields are the text, the multi-line text and the label fields, and the rich
  text fields unless `:include-richtext?` is false in the context."
  [context]
  (let [query-field-paths (when (contains? context :query-field-paths)
                            (->> (:query-field-paths context)
                                 (keep normalize-field-path)
                                 set))
        options {:include-richtext? (not (false? (:include-richtext? context)))}]
    (->> (or (:accessible-fields context) [])
         (keep (fn [{:keys [field-path] :as field}]
                 (let [normalized-field-path (normalize-field-path field-path)
                       data-type (or (:field/data-type field)
                                     (:data-type field))]
                   (when (and normalized-field-path
                              (or (nil? query-field-paths)
                                  (contains? query-field-paths normalized-field-path)))
                     (field-path->query-field normalized-field-path data-type options)))))
         distinct
         vec)))

(defn searchable-trigram-fields
  "The trigram fields of the accessible short text fields in `context`, with the
   same scope as `searchable-query-fields`. See `common.opensearch.trigram`."
  [context]
  (let [query-field-paths (when (contains? context :query-field-paths)
                            (->> (:query-field-paths context)
                                 (keep normalize-field-path)
                                 set))]
    (->> (or (:accessible-fields context) [])
         (keep (fn [{:keys [field-path] :as field}]
                 (let [normalized-field-path (normalize-field-path field-path)
                       data-type (or (:field/data-type field)
                                     (:data-type field))]
                   (when (and normalized-field-path
                              ;; Only a field of the entity itself has trigrams.
                              (= 1 (count normalized-field-path))
                              (contains? trigram/data-types data-type)
                              (or (nil? query-field-paths)
                                  (contains? query-field-paths normalized-field-path)))
                     (trigram/field (os-utils/field-path->flat-alias normalized-field-path))))))
         distinct
         vec)))

(def ^:private primary-query-field-boost 10)

(defn- primary-query-field
  [entity-type]
  (when-let [field-path (some-> (get-in (search-config entity-type) [:result/title-attr])
                                normalize-field-path)]
    (field-path->query-field field-path
                             (:field/data-type (entity-field-schema entity-type field-path))
                             {})))

(defn title-query-field
  "The field of the title of `entity-type` that the search looks in, for example
   `flat.oppo-name`, or nil."
  [entity-type]
  (primary-query-field entity-type))

(defn- boost-primary-query-field
  [fields entity-type]
  (let [primary-field (primary-query-field entity-type)]
    (mapv (fn [field]
            (if (= field primary-field)
              (str field "^" primary-query-field-boost)
              field))
          fields)))

(defn- query-words
  "The words of a query: the parts between the spaces."
  [query-text]
  (vec (re-seq #"\S+" query-text)))

(def ^:private short-word-length
  "A word of up to this many characters is searched in the title and in the label
   fields only. A short word is in nearly every free text, so in all the fields it
   would find nearly every row. The label fields stay, so that `DD` finds the
   stage DD and `EUR` the currency (DEV-8217, item 15)."
  3)

(def ^:private fuzzy-boost
  "The rank of a match with a typo is below every other match."
  0.2)

(defn- word-start-clause
  "A clause that finds a text that has a word that starts with `word`, in any of
   `fields`. `bool_prefix` matches each term that starts with the word. The type
   `phrase_prefix` that was used before looked at 50 terms only, in the order of
   the whole index of all the tenants, so it could miss the deal (DEV-8226,
   item 21). The analyzer can split a word, for example `Import-test` into
   `import` and `test`. All the parts must match, as all the words must."
  [fields word]
  {:multi_match {:query    word
                 :fields   fields
                 :type     "bool_prefix"
                 :operator "and"}})

(defn- inside-word-clause
  "A clause that finds a text that has `word` inside a word, in any of the trigram
   fields `trigram-fields`. See `common.opensearch.trigram`."
  [trigram-fields word]
  {:multi_match {:query  word
                 :fields trigram-fields
                 :type   "phrase"}})

(defn- fuzzy-title-clause
  "A clause that finds a title with `word` in it with a typo or two. A word of
   fewer than 4 characters must have no typo, and the first character must be
   right."
  [title-field word]
  {:match {title-field {:query          word
                        :fuzziness      "AUTO:4,7"
                        :prefix_length  1
                        :operator       "and"
                        :boost          fuzzy-boost}}})

(defn- any-of
  [clauses]
  (let [clauses (vec (remove nil? clauses))]
    (if (= 1 (count clauses))
      (first clauses)
      {:bool {:should               clauses
              :minimum_should_match 1}})))

(defn- word-clause
  "A clause that finds a text with `word` in it. The last word of a query that a
   user types is not complete, and the words before it are matched in the same
   way, so that every word works as the start of a word.
   - A word of 1 character must start a word of the title.
   - A word of 2 characters must start a word of the title or of a label field.
   - A word of 3 characters must also do so, or be inside a word of the title.
   - A longer word must start a word of any field, or be in a short text field,
     or be in the title with a typo.
   The start of a word is matched also when there are trigrams: an index that is
   older than the trigrams has none."
  [{:keys [fields short-word-fields title-field title-trigram-field trigram-fields]} word]
  (let [length (count word)
        ;; A listing with no visible title searches every word in all its fields.
        short-word? (and (some? title-field)
                         (<= length short-word-length))]
    (cond
      (and (some? title-field) (= 1 length))
      {:prefix {title-field {:value (string/lower-case word)}}}

      (and short-word? (< length trigram/min-length))
      (word-start-clause short-word-fields word)

      short-word?
      (any-of [(word-start-clause short-word-fields word)
               (when title-trigram-field
                 (inside-word-clause [title-trigram-field] word))])

      :else
      (any-of [(word-start-clause fields word)
               (when (and (seq trigram-fields)
                          (<= trigram/min-length length))
                 (inside-word-clause trigram-fields word))
               (when (some? title-field)
                 (fuzzy-title-clause title-field word))]))))

(defn- title-rank-clauses
  "The clauses that rank a title that is the query, or starts with it, above a
   title that only has its words."
  [title-field query-text]
  (when title-field
    (let [lower-field (str title-field ".lower")
          lower-text (string/lower-case query-text)]
      [{:term {lower-field {:value lower-text
                            :boost 100}}}
       {:prefix {lower-field {:value lower-text
                              :boost 50}}}])))

(defn toolbar-query-clauses
  "The clauses of the search of the toolbar for `query-text`.
   A query of one character is searched in the title only, as a single letter in
   every column finds nearly every row. Each word of the query must be found, see
   `word-clause`: a short word in the title, a longer word in all the visible text
   fields. The words can be in different fields, so that `Manisha hold` finds the
   deals of the owner Manisha that are on hold, and each word that is added makes
   the result smaller. The rank is highest for a title that is the query, then for
   a title that starts with it, then for a word that starts with a word of the
   query, then for a word that has it inside, and last for a typo. A text that has
   the whole query as a phrase has a higher rank. A query that has the name of an
   HTML tag is not searched in the rich text fields: see `html-tag-query?`."
  [query-text context]
  (let [query-text (string/trim (or query-text ""))
        searchable-fields (searchable-query-fields
                           (assoc context :include-richtext? (not (html-tag-query? query-text))))
        primary-field (primary-query-field (:entity-type context))
        title-field (some #{primary-field} searchable-fields)
        short-primary-field (when (<= (count query-text) 1)
                              title-field)
        fields (boost-primary-query-field searchable-fields (:entity-type context))
        trigram-fields (searchable-trigram-fields context)
        title-trigram-field (some #{(trigram/field title-field)} trigram-fields)
        word-fields {:fields              fields
                     :short-word-fields   (filterv (fn [field]
                                                     (or (= field (some-> title-field (str "^" primary-query-field-boost)))
                                                         (string/starts-with? field "labels.")))
                                                   fields)
                     :title-field         title-field
                     :title-trigram-field title-trigram-field
                     :trigram-fields      trigram-fields}
        words (query-words query-text)]
    (cond
      (or (empty? query-text)
          (empty? fields))
      []

      short-primary-field
      [{:bool
        {:should [{:prefix {short-primary-field
                            {:value (string/lower-case query-text)
                             :boost 100}}}]
         :minimum_should_match 1}}]

      :else
      [{:bool
        {:must   (mapv (partial word-clause word-fields) words)
         :should (cond-> (vec (title-rank-clauses title-field (string/join " " words)))
                   (< 1 (count words))
                   (conj {:multi_match {:query  (string/join " " words)
                                        :fields fields
                                        :type   "phrase_prefix"
                                        :boost  25}}))}}])))

(defn toolbar-filter-clauses
  [filters-by-field-path context]
  (let [data-types-by-path (os-filter-queries/field-data-type-by-path context)]
    (->> (or filters-by-field-path {})
         (keep (fn [[field-path field-filter]]
                 (let [normalized-field-path (normalize-field-path field-path)
                       data-type (get data-types-by-path normalized-field-path)]
                   (when normalized-field-path
                     (os-filter-queries/field-filter->clause normalized-field-path data-type field-filter)))))
         vec)))

(defn default-columns
  [entity-type]
  (vec (or (get-in (search-config entity-type) [:list/default-columns]) [])))

(defn po-default-field-paths
  [entity-type]
  (let [configured-paths (or (get-in (view-config entity-type :grid) [:po-default-field-paths])
                             (default-columns entity-type))]
    (->> configured-paths
         (map normalize-field-path)
         (remove nil?)
         vec)))

(defn grid-mandatory-field-paths
  [entity-type]
  (->> (or (get-in (view-config entity-type :grid) [:mandatory-field-paths]) [])
       (map normalize-field-path)
       (remove nil?)
       vec))

(defn grid-pinned-field-paths
  [entity-type side]
  (->> (or (get-in (view-config entity-type :grid) [:pinned-field-paths side]) [])
       (map normalize-field-path)
       (remove nil?)
       vec))

(defn quick-filters
  [entity-type]
  (vec (or (get-in (search-config entity-type) [:quick-filters]) [])))

(defn- default-card-config
  [entity-type]
  (let [search (search-config entity-type)
        header-field (or (:result/title-attr search)
                         (first (:list/default-columns search)))
        footer-field (when-let [default-columns (seq (:list/default-columns search))]
                       (last default-columns))
        body-fields (->> (concat (:result/subtitle-attrs search)
                                 (:list/default-columns search))
                         (remove nil?)
                         distinct
                         (remove (fn [field]
                                   (or (= field header-field)
                                       (= field footer-field))))
                         (take 3)
                         vec)]
    {:header-fields (vec (remove nil? [header-field]))
     :body-fields body-fields
     :footer-fields (vec (remove nil? [footer-field]))
     ;; The fallback field for the title of a card, when the header field has no
     ;; value. For example a task shows its type when it has no title. Nil when
     ;; the entity type defines none, see `:result/title-fallback-attr`.
     :title-fallback (:result/title-fallback-attr search)}))

(defn card-config
  [entity-type view-mode]
  (let [defaults (default-card-config entity-type)
        configured (get-in (view-config entity-type view-mode) [:card])]
    (merge defaults (or configured {}))))

(defn card-config-field-paths
  [card-config]
  (->> (concat (:header-fields card-config)
               (:body-fields card-config)
               (:footer-fields card-config)
               (:meta-fields card-config)
               (when-let [highlight (:highlight card-config)]
                 [highlight])
               (when-let [title-fallback (:title-fallback card-config)]
                 [title-fallback]))
       (keep normalize-field-path)
       distinct
       vec))

(defn kanban-group-by-field
  [entity-type]
  (or (get-in (view-config entity-type :kanban) [:group-by-field])
      (first (quick-filters entity-type))))

(defn- sort-clauses
  "The sort clauses for one field. A text value sorts without regard to case. See
   `os-utils/text-sort-fields`."
  [entity-type [field direction]]
  (let [field-path (normalize-field-path field)
        field-schema (entity-field-schema entity-type field-path)
        data-type (:field/data-type field-schema)
        sort-fields (cond
                      (contains? os-fields/labelled-data-types data-type)
                      (os-utils/text-sort-fields (os-utils/field-path->labels-alias field-path))

                      (os-fields/is-text-field? data-type)
                      (os-utils/text-sort-fields (os-utils/field-path->flat-alias field-path))

                      :else
                      [(os-utils/field-path->flat-alias field-path)])]
    (mapv (fn [sort-field]
            {sort-field {:order (name direction)}})
          sort-fields)))

(defn default-sort
  [entity-type]
  (when-let [sort-v (get-in (search-config entity-type) [:list/default-sort])]
    (sort-clauses entity-type sort-v)))

(def ^:private relevance-sort-clause
  {"_score" {:order "desc"}})

(defn relevance-first-sort
  "The sort of a request with a default sort. With query text, the best match is
   first and the sort only orders the matches that score the same. Use this for a
   sort that the user did not choose. See `sort-with-relevance` for a chosen sort."
  [query-text sort-clauses]
  (if (string/blank? (string/trim (or query-text "")))
    sort-clauses
    (into [relevance-sort-clause]
          (or sort-clauses []))))

(defn sort-with-relevance
  "The sort of a request with a sort that the user chose, for example a column of
   the grid or the card sort of the Kanban board. The chosen sort comes first, also
   when there is query text. The relevance only orders the rows that the chosen sort
   does not tell apart. Without a chosen sort, query text ranks by relevance."
  [query-text chosen-sort-clauses]
  (cond
    (string/blank? (string/trim (or query-text "")))
    chosen-sort-clauses

    (seq chosen-sort-clauses)
    (conj (vec chosen-sort-clauses) relevance-sort-clause)

    :else
    [relevance-sort-clause]))

(defn- kanban-card-sort
  [entity-type sort-mode]
  (when-let [title-field (first (:header-fields (card-config entity-type :kanban)))]
    (case (kanban-sort/normalize-card-sort-mode sort-mode)
      :sort-by/alphanumerical (sort-clauses entity-type [title-field :asc])
      :sort-by/value-desc (sort-clauses entity-type [title-field :desc])
      nil)))

(defn search-results-config
  [{:keys [entity-type context query-text toolbar-filters-by-field-path pipeline-id view-mode
           row-group-by-field row-group-sort-mode hide-empty-columns? kanban-column-sort-mode]
    :or {view-mode :list}}]
  (let [query-context (assoc (or context {}) :entity-type entity-type)]
    {:entity-type entity-type
     :pipeline-id pipeline-id
     :group-by-field row-group-by-field
     :row-group-sort-mode (kanban-sort/normalize-row-sort-mode row-group-sort-mode)
     :kanban-column-sort-mode (kanban-sort/normalize-column-sort-mode kanban-column-sort-mode)
     :hide-empty-columns? (boolean hide-empty-columns?)
     :opensearch-queries (vec (concat (toolbar-filter-clauses toolbar-filters-by-field-path query-context)
                                      (toolbar-query-clauses query-text query-context)))
     :sort (relevance-first-sort query-text (default-sort entity-type))
     :card-config (card-config entity-type view-mode)}))

(def ^:private sum-data-types
  #{:data.type/currency-value
    :data.type/number
    :data.type/integer
    :data.type/long
    :data.type/percentage
    :data.type/multiplier})

(defn sum-field?
  "True for a field that a Kanban column can sum: a number field."
  [data-type]
  (contains? sum-data-types data-type))

(defn kanban-sum-totals
  "The total that the Kanban columns and swimlanes show for the field at
   `field-path`, as `common.opensearch.totals` describes it, in a vector, or nil.
   `field-schema` is the schema of the field in the tenant. A percentage and a
   multiple are averaged, as their sum means nothing. The id names the kind and
   the field, so that a total of another field that is still on screen is not
   shown as the total of the field that the user chose."
  [field-path field-schema]
  (let [field-path (normalize-field-path field-path)
        data-type (:field/data-type field-schema)
        secondary-attribute (:field/secondary-attribute field-schema)
        flat-field (some-> field-path os-utils/field-path->flat-alias)
        kind (cond
               (some? (:field/total-kind field-schema))
               (name (:field/total-kind field-schema))

               (contains? #{:data.type/percentage :data.type/multiplier} data-type)
               "avg"

               :else
               "sum")]
    (when (and field-path
               (sum-field? data-type)
               ;; A field that shows the days since a time has no value in the
               ;; index to sum, also when a saved view still names it.
               (nil? (days-since/since-alias flat-field)))
      [(cond-> {:id        (str kind ":" flat-field)
                :field     flat-field
                :kind      kind
                ;; For the text of the total. The search does not read it.
                :data-type data-type}
         (and (= :data.type/currency-value data-type)
              (keyword? secondary-attribute))
         (assoc :currency-field (os-utils/field-path->flat-alias
                                 (conj (pop (vec field-path)) secondary-attribute))))])))

(defn kanban-config
  [{:keys [entity-type context query-text toolbar-filters-by-field-path pipeline-id group-by-field row-group-by-field hide-empty-columns?
           row-group-sort-mode kanban-column-sort-mode kanban-card-sort-mode sum-totals]}]
  (let [query-context (assoc (or context {}) :entity-type entity-type)]
    {:entity-type entity-type
     :group-by-field group-by-field
     :row-group-by-field row-group-by-field
     :row-group-sort-mode (kanban-sort/normalize-row-sort-mode row-group-sort-mode)
     :kanban-column-sort-mode (kanban-sort/normalize-column-sort-mode kanban-column-sort-mode)
     :hide-empty-columns? (boolean hide-empty-columns?)
     :pipeline-id pipeline-id
     :opensearch-queries (vec (concat (toolbar-filter-clauses toolbar-filters-by-field-path query-context)
                                      (toolbar-query-clauses query-text query-context)))
     :sort (sort-with-relevance query-text
                                (kanban-card-sort entity-type kanban-card-sort-mode))
     :card-config (card-config entity-type :kanban)
     ;; The cards mark the words of the search, see `common.opensearch.highlight`.
     :query-text query-text
     ;; The sum that the columns and the swimlanes show, see `kanban-sum-totals`.
     :totals sum-totals}))
